import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ShieldAlert, Activity, Lock, UserX, Globe, AlertTriangle, RefreshCw, Eye, Zap, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";
import { useITPortalI18n } from "../../i18n/ITPortalI18nContext";

const POLL_INTERVAL_MS = 30000;
const SEC_I18N = {
 "IT": {
  "attempts": "tentativi",
  "expires_now": "scade ora",
  "load_err": "Errore caricamento health",
  "unlocked": "sbloccato",
  "unlock_err": "Sblocco non riuscito",
  "err": "Errore",
  "loc": "it-IT",
  "snap": "Snapshot live · ultimo refresh: {g} · auto ogni 30s",
  "healthy": "🟢 Stato: sano",
  "incidents": "🔴 Incidenti attivi",
  "anomal": "🟡 Attività anomala",
  "refresh": "Aggiorna ora",
  "dry_title": "🟡 Modalità DRY-RUN attiva",
  "dry_a": "Gli alert email e l'audit log sono attivi, ma i lockout effettivi NON sono applicati. Quando i KPI sopra mostrano pattern stabili (no falsi positivi su utenti legittimi) imposta",
  "dry_b": "in",
  "dry_c": "e riavvia il backend per attivare l'enforcement.",
  "last24": "Ultime 24 ore",
  "failed": "Login falliti",
  "email_from": "{e} email da {i} IP",
  "lockouts": "Blocchi attivati",
  "lock_sub": "account + IP + owner probe",
  "cross": "Tentativi cross-Owner",
  "cross_sub": "trigger honeypot admin",
  "autolock": "Admin auto-bloccati",
  "autolock_sub": "eventi di auto-contenimento",
  "active_now": "Incidenti attivi ora",
  "acc_locked": "Account bloccati",
  "threshold": "soglia {a} / {b} min",
  "ip_locked": "IP bloccati",
  "ip_sub": "≥{a} email distinte / {b} min",
  "probe_now": "Owner probe in corso",
  "probe_sub": "≥{a} fail / {b} min",
  "to_unlock": "da sbloccare manualmente",
  "timeline": "Timeline tentativi falliti — ultime 24h",
  "low": "basso",
  "mid": "medio",
  "high": "alto",
  "expires_in": "scade in",
  "unlock": "Sblocca",
  "probes": "Owner sotto probe",
  "manual": "Sblocco manuale da Gestione Utenti.",
  "top_ip": "{S.top_ip}",
  "no_ip": "Nessun IP rilevato.",
  "top_mail": "{S.top_mail}",
  "no_target": "Nessun target rilevato.",
  "attempts_en": "tentativi"
 },
 "EN": {
  "attempts": "attempts",
  "expires_now": "expires now",
  "load_err": "Failed to load security health",
  "unlocked": "unlocked",
  "unlock_err": "Unlock failed",
  "err": "Error",
  "loc": "en-GB",
  "snap": "Live snapshot · last refresh: {g} · auto every 30s",
  "healthy": "🟢 Status: healthy",
  "incidents": "🔴 Active incidents",
  "anomal": "🟡 Unusual activity",
  "refresh": "Refresh now",
  "dry_title": "🟡 DRY-RUN mode active",
  "dry_a": "Email alerts and the audit log are active, but lockouts are NOT enforced. When the KPIs above show stable patterns (no false positives on legitimate users) set",
  "dry_b": "in",
  "dry_c": "and restart the backend to enable enforcement.",
  "last24": "Last 24 hours",
  "failed": "Failed logins",
  "email_from": "{e} emails from {i} IPs",
  "lockouts": "Lockouts triggered",
  "lock_sub": "account + IP + owner probe",
  "cross": "Cross-Owner attempts",
  "cross_sub": "admin honeypot triggers",
  "autolock": "Admin auto-locked",
  "autolock_sub": "auto-containment events",
  "active_now": "Active incidents now",
  "acc_locked": "Locked accounts",
  "threshold": "threshold {a} / {b} min",
  "ip_locked": "Blocked IPs",
  "ip_sub": "≥{a} distinct emails / {b} min",
  "probe_now": "Owner probes in progress",
  "probe_sub": "≥{a} fails / {b} min",
  "to_unlock": "to be unlocked manually",
  "timeline": "Failed attempts timeline — last 24h",
  "low": "low",
  "mid": "medium",
  "high": "high",
  "expires_in": "expires in",
  "unlock": "Unlock",
  "probes": "Owners under probe",
  "manual": "Manual unlock from User Management.",
  "top_ip": "Top attacking IPs (24h)",
  "no_ip": "No IPs detected.",
  "top_mail": "Top targeted emails (24h)",
  "no_target": "No targets detected.",
  "attempts_en": "attempts"
 }
};
const fill = (str, o) => Object.entries(o).reduce((a, [k, v]) => a.replace(`{${k}}`, v), str);


// Token resolution: support both the IT Portal (uses ``it_token`` in
// localStorage) and the regular admin app (uses ``token``). This lets
// us drop the panel inside either context without depending on the
// AuthContext, which the IT Portal does not wire up.
const getAuthToken = () => {
  return localStorage.getItem("it_token") || localStorage.getItem("token") || "";
};
const authHeader = () => {
  const t = getAuthToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const KpiCard = ({ icon: Icon, label, value, sublabel, tone = "neutral", testid }) => {
  const tones = {
    neutral: { bg: "bg-[#0f0f12]", ring: "border-gray-800", icon: "text-gray-400", value: "text-white" },
    info: { bg: "bg-[#0a0f1a]", ring: "border-blue-500/30", icon: "text-blue-400", value: "text-blue-300" },
    warn: { bg: "bg-[#1a1500]", ring: "border-lojack-clause-500/40", icon: "text-lojack-clause-400", value: "text-lojack-clause-300" },
    danger: { bg: "bg-[#1a0a0a]", ring: "border-red-500/50", icon: "text-red-400", value: "text-red-300" },
    success: { bg: "bg-[#0a1a0e]", ring: "border-lojack-blue-500/30", icon: "text-lojack-blue-400", value: "text-lojack-blue-300" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div className={`${t.bg} ${t.ring} border rounded-2xl p-4 flex flex-col gap-2 transition-colors`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-gray-400 text-[11px] uppercase tracking-wider">{label}</span>
        <Icon size={16} className={t.icon} />
      </div>
      <p className={`text-3xl font-bold ${t.value} tabular-nums`}>{value}</p>
      {sublabel && <p className="text-gray-500 text-[11px] leading-tight">{sublabel}</p>}
    </div>
  );
};

const HourlyBars = ({ buckets, S }) => {
  const max = Math.max(1, ...buckets.map(b => b.count));
  return (
    <div className="flex items-end justify-between gap-[2px] h-32" data-testid="hourly-bars">
      {buckets.map((b, idx) => {
        const h = Math.max(2, Math.round((b.count / max) * 100));
        const tone = b.count === 0 ? 'bg-gray-800' : b.count >= max * 0.66 ? 'bg-red-500/80' : b.count >= max * 0.33 ? 'bg-lojack-clause-500/70' : 'bg-blue-500/60';
        return (
          <div key={b.key} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="relative w-full flex-1 flex items-end" title={`${b.label} → ${b.count} ${S.attempts}`}>
              <div className={`w-full ${tone} rounded-t transition-all`} style={{ height: `${h}%` }}></div>
              {b.count > 0 && (
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-gray-500 opacity-0 group-hover:opacity-100 whitespace-nowrap">{b.count}</span>
              )}
            </div>
            {idx % 4 === 0 && <span className="text-[9px] text-gray-600 -rotate-45 origin-top-left absolute mt-32">{b.label}</span>}
          </div>
        );
      })}
    </div>
  );
};

const fmtRemaining = (sec, S) => {
  if (sec <= 0) return S.expires_now;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  return `${m}min ${s}s`;
};

export const SecurityHealthPanel = () => {
  const { locale } = useITPortalI18n();
  const S = SEC_I18N[locale === "EN" ? "EN" : "IT"];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchHealth = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/security/health`, authHeader());
      setData(r.data); setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || S.load_err);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  const unlock = async (payload, label) => {
    try {
      await axios.post(`${API}/admin/security/unlock`, payload, authHeader());
      toast.success(`${label} ${S.unlocked}`);
      fetchHealth();
    } catch (e) { toast.error(e?.response?.data?.detail || S.unlock_err); }
  };

  useEffect(() => {
    fetchHealth();
    timerRef.current = setInterval(() => fetchHealth(), POLL_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-10 flex justify-center" data-testid="security-health-loading">
      <div className="spinner" />
    </div>
  );

  if (error || !data) return (
    <div className="bg-red-500/10 border border-red-500/40 rounded-2xl p-6" data-testid="security-health-error">
      <p className="text-red-300 text-sm">{error || S.err}</p>
    </div>
  );

  const k = data.kpi;
  const cfg = data.config;
  const generated = new Date(data.generated_at).toLocaleString(S.loc, { hour:'2-digit', minute:'2-digit', second:'2-digit' });
  const isHealthy = k.failed_logins_24h < 5 && k.active_account_lockouts === 0 && k.active_ip_lockouts === 0 && k.active_owner_probes === 0 && k.admin_auto_locked_total === 0;
  const hasIncidents = k.active_account_lockouts > 0 || k.active_ip_lockouts > 0 || k.active_owner_probes > 0 || k.admin_auto_locked_total > 0;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="security-health-panel">
      {/* Header */}
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isHealthy ? 'bg-lojack-blue-500/15 text-lojack-blue-400' : hasIncidents ? 'bg-red-500/15 text-red-400 animate-pulse' : 'bg-lojack-clause-500/15 text-lojack-clause-400'}`}>
            <ShieldAlert size={22} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Security Health</h2>
            <p className="text-gray-500 text-xs">{fill(S.snap, { g: generated })}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${isHealthy ? 'bg-lojack-blue-500/15 text-lojack-blue-400 border-lojack-blue-500/30' : hasIncidents ? 'bg-red-500/15 text-red-400 border-red-500/40' : 'bg-lojack-clause-500/15 text-lojack-clause-400 border-lojack-clause-500/30'}`} data-testid="security-health-status">
            {isHealthy ? S.healthy : hasIncidents ? S.incidents : S.anomal}
          </span>
          <button onClick={() => fetchHealth(true)} disabled={refreshing} className="p-2 bg-[#1a1a1a] hover:bg-[#222] border border-gray-700 rounded-lg text-gray-400 transition-colors" title={S.refresh} data-testid="security-health-refresh">
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Dry-run banner */}
      {cfg.dry_run && (
        <div className="bg-lojack-clause-500/10 border-2 border-lojack-clause-500/50 rounded-2xl p-4 flex items-start gap-3" data-testid="security-dry-run-banner">
          <AlertTriangle size={20} className="text-lojack-clause-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-lojack-clause-300 font-semibold text-sm">{S.dry_title}</p>
            <p className="text-lojack-clause-200/80 text-xs mt-1 leading-relaxed">
              {S.dry_a} <code className="bg-lojack-clause-500/10 px-1.5 py-0.5 rounded">LOGIN_LOCKOUT_DRY_RUN=false</code> {S.dry_b} <code className="bg-lojack-clause-500/10 px-1.5 py-0.5 rounded">backend/.env</code> {S.dry_c}
            </p>
          </div>
        </div>
      )}

      {/* KPI grid (4 cards row 1, 4 cards row 2) */}
      <div>
        <p className="text-gray-400 text-xs uppercase tracking-wider mb-3 font-semibold">{S.last24}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard icon={Activity} label={S.failed} value={k.failed_logins_24h} sublabel={fill(S.email_from, { e: k.distinct_emails_targeted_24h, i: k.distinct_ips_24h })} tone={k.failed_logins_24h > 50 ? 'danger' : k.failed_logins_24h > 10 ? 'warn' : 'neutral'} testid="kpi-failed-24h" />
          <KpiCard icon={Lock} label={S.lockouts} value={k.login_lockouts_24h} sublabel={S.lock_sub} tone={k.login_lockouts_24h > 0 ? 'warn' : 'neutral'} testid="kpi-lockouts-24h" />
          <KpiCard icon={UserX} label={S.cross} value={k.cross_owner_attempts_24h} sublabel={S.cross_sub} tone={k.cross_owner_attempts_24h > 0 ? 'danger' : 'neutral'} testid="kpi-cross-owner" />
          <KpiCard icon={Zap} label={S.autolock} value={k.admin_auto_locks_24h} sublabel={S.autolock_sub} tone={k.admin_auto_locks_24h > 0 ? 'danger' : 'neutral'} testid="kpi-admin-locks" />
        </div>
      </div>

      <div>
        <p className="text-gray-400 text-xs uppercase tracking-wider mb-3 font-semibold">{S.active_now}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard icon={Lock} label={S.acc_locked} value={k.active_account_lockouts} sublabel={fill(S.threshold, { a: cfg.login_account_threshold, b: cfg.login_account_window_min })} tone={k.active_account_lockouts > 0 ? 'danger' : 'success'} testid="kpi-active-accounts" />
          <KpiCard icon={Globe} label={S.ip_locked} value={k.active_ip_lockouts} sublabel={fill(S.ip_sub, { a: cfg.login_ip_distinct_emails, b: cfg.login_ip_window_min })} tone={k.active_ip_lockouts > 0 ? 'danger' : 'success'} testid="kpi-active-ips" />
          <KpiCard icon={AlertTriangle} label={S.probe_now} value={k.active_owner_probes} sublabel={fill(S.probe_sub, { a: cfg.owner_probe_threshold, b: cfg.owner_probe_window_min })} tone={k.active_owner_probes > 0 ? 'danger' : 'success'} testid="kpi-active-owner-probes" />
          <KpiCard icon={UserX} label={S.autolock} value={k.admin_auto_locked_total} sublabel={S.to_unlock} tone={k.admin_auto_locked_total > 0 ? 'danger' : 'success'} testid="kpi-admin-locked-now" />
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-blue-400" />
            <h3 className="text-white font-semibold text-sm">{S.timeline}</h3>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-800 rounded-sm"></span>0</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500/60 rounded-sm"></span>{S.low}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-lojack-clause-500/70 rounded-sm"></span>{S.mid}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500/80 rounded-sm"></span>{S.high}</span>
          </div>
        </div>
        <HourlyBars buckets={data.timeline_24h} S={S} />
        <div className="flex justify-between mt-2 text-[10px] text-gray-500">
          <span>{data.timeline_24h[0]?.label || '—'}</span>
          <span>{data.timeline_24h[12]?.label || '—'}</span>
          <span>{data.timeline_24h[23]?.label || '—'}</span>
        </div>
      </div>

      {/* Active incidents details */}
      {hasIncidents && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.active_lockouts.accounts.length > 0 && (
            <div className="bg-[#1a0a0a] border border-red-500/40 rounded-2xl p-4" data-testid="active-accounts-card">
              <p className="text-red-300 font-semibold text-sm mb-2 flex items-center gap-2"><Lock size={14} /> {S.acc_locked} ({data.active_lockouts.accounts.length})</p>
              <ul className="space-y-1.5 text-xs">
                {data.active_lockouts.accounts.map((l, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-gray-300">
                    <span className="font-mono truncate">{l.email}</span>
                    <span className="text-lojack-clause-300 whitespace-nowrap">{l.count} fail · {S.expires_in} {fmtRemaining(l.seconds_remaining, S)}</span>
                    <button onClick={() => unlock({ email: l.email }, l.email)} data-testid={`unlock-account-${l.email}`}
                      className="px-2 py-0.5 rounded-md border border-red-400/50 text-red-200 hover:bg-red-500 hover:text-white text-[11px]">{S.unlock}</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.active_lockouts.ips.length > 0 && (
            <div className="bg-[#1a0a0a] border border-red-500/40 rounded-2xl p-4" data-testid="active-ips-card">
              <p className="text-red-300 font-semibold text-sm mb-2 flex items-center gap-2"><Globe size={14} /> {S.ip_locked} ({data.active_lockouts.ips.length})</p>
              <ul className="space-y-1.5 text-xs">
                {data.active_lockouts.ips.map((l, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-gray-300">
                    <span className="font-mono">{l.ip}</span>
                    <span className="text-lojack-clause-300 whitespace-nowrap">{l.distinct_emails} email · {S.expires_in} {fmtRemaining(l.seconds_remaining, S)}</span>
                    <button onClick={() => unlock({ ip: l.ip }, `IP ${l.ip}`)} data-testid={`unlock-ip-${l.ip}`}
                      className="px-2 py-0.5 rounded-md border border-red-400/50 text-red-200 hover:bg-red-500 hover:text-white text-[11px]">{S.unlock}</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.active_lockouts.owner_probes.length > 0 && (
            <div className="bg-[#1a0a0a] border border-red-500/40 rounded-2xl p-4" data-testid="active-owner-probes-card">
              <p className="text-red-300 font-semibold text-sm mb-2 flex items-center gap-2"><AlertTriangle size={14} /> {S.probes} ({data.active_lockouts.owner_probes.length})</p>
              <ul className="space-y-1.5 text-xs">
                {data.active_lockouts.owner_probes.map((l, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-gray-300">
                    <span className="font-mono truncate">{l.email}</span>
                    <span className="text-lojack-clause-300 whitespace-nowrap">{l.count} fail</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.active_lockouts.admin_auto_locked.length > 0 && (
            <div className="bg-[#1a0a0a] border border-red-500/40 rounded-2xl p-4" data-testid="active-admin-locked-card">
              <p className="text-red-300 font-semibold text-sm mb-2 flex items-center gap-2"><UserX size={14} /> {S.autolock} ({data.active_lockouts.admin_auto_locked.length})</p>
              <ul className="space-y-1.5 text-xs">
                {data.active_lockouts.admin_auto_locked.map((u, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-gray-300">
                    <span className="font-mono truncate">{u.email}</span>
                    <span className="text-lojack-clause-300 whitespace-nowrap">{u.lockout_trigger_count || '—'} attempts</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-500 text-[10px] mt-3 italic">{S.manual}</p>
            </div>
          )}
        </div>
      )}

      {/* Top lists */}
      {(data.top_attackers.length > 0 || data.top_targets.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-4" data-testid="top-attackers-card">
            <p className="text-gray-300 font-semibold text-sm mb-3 flex items-center gap-2"><Eye size={14} className="text-red-400" /> {S.top_ip}</p>
            {data.top_attackers.length === 0 ? (
              <p className="text-gray-600 text-xs">{S.no_ip}</p>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {data.top_attackers.map((a, i) => (
                  <li key={i} className="flex items-center justify-between gap-3">
                    <span className="text-gray-400">#{i + 1}</span>
                    <span className="flex-1 font-mono text-gray-200 truncate ml-2">{a.value || '—'}</span>
                    <span className="text-red-400 font-semibold whitespace-nowrap">{a.count} fails</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-4" data-testid="top-targets-card">
            <p className="text-gray-300 font-semibold text-sm mb-3 flex items-center gap-2"><Eye size={14} className="text-lojack-clause-400" /> {S.top_mail}</p>
            {data.top_targets.length === 0 ? (
              <p className="text-gray-600 text-xs">{S.no_target}</p>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {data.top_targets.map((t, i) => (
                  <li key={i} className="flex items-center justify-between gap-3">
                    <span className="text-gray-400">#{i + 1}</span>
                    <span className="flex-1 font-mono text-gray-200 truncate ml-2">{t.value}</span>
                    <span className="text-lojack-clause-400 font-semibold whitespace-nowrap">{t.count} fails</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Footer config summary */}
      <div className="bg-[#0a0a0f] border border-gray-900 rounded-xl p-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[10px] text-gray-500">
        <span>🛡️ Owners: <strong className="text-gray-400">{cfg.platform_owners_count}</strong></span>
        <span>📧 Alert recipients: <strong className="text-gray-400">{cfg.alert_recipients_count}</strong></span>
        <span>🌐 Trusted IPs: <strong className="text-gray-400">{cfg.trusted_ips_count}</strong></span>
        <span>🔐 Account lockout: <strong className="text-gray-400">{cfg.login_account_threshold}/{cfg.login_account_window_min}min → {cfg.login_account_lockout_min}min</strong></span>
        <span>🌍 IP scanner: <strong className="text-gray-400">{cfg.login_ip_distinct_emails} email/{cfg.login_ip_window_min}min → {cfg.login_ip_lockout_min}min</strong></span>
        <span>🚨 Cross-owner auto-lock: <strong className="text-gray-400">{cfg.cross_owner_threshold} attempts/{cfg.cross_owner_window_min}min</strong></span>
      </div>
    </div>
  );
};
